/**
 * Statut d'une vente flash à partir de sa date et de ses horaires, en HEURE DE PARIS
 * (le restaurateur les saisit ainsi) quel que soit le fuseau de l'ordinateur.
 * Copie côté mobile : apps/mobile/src/utils/flashPromo.ts — garder les deux alignées. Même
 * calcul que les règles Firestore (bloc « heure de Paris » de firestore.rules), qui refusent
 * la réservation d'une offre terminée. Hypothèse : France métropolitaine (outre-mer non géré).
 */

export type FlashPromoStatus = 'upcoming' | 'live' | 'soldout' | 'ended' | 'stopped';

interface FlashPromoTiming {
    active?: boolean;
    quantityRemaining?: number;
    date?: string;      // "YYYY-MM-DD", jour de Paris
    startTime?: string; // "HH:mm", heure de Paris
    endTime?: string;   // "HH:mm", heure de Paris
    createdAt?: unknown; // Timestamp Firestore, ISO string ou Date
}

// ── Heure de Paris : heure d'été de l'UE du dernier dimanche de mars, 02:00, au dernier
// dimanche d'octobre, 03:00 (heures murales) ; UTC+2, sinon UTC+1. Heure inexistante ou
// ambiguë : comptée en heure d'été, comme dans les règles.
const HOUR_MS = 3_600_000;
const pad2 = (n: number) => String(n).padStart(2, '0');

const lastSundayUtc = (year: number, month: number): number => {
    const last = Date.UTC(year, month - 1, 31);
    // 1970-01-01 était un jeudi : (jours depuis 1970 + 4) % 7 == 0 un dimanche.
    return Date.UTC(year, month - 1, 31 - ((Math.floor(last / 86_400_000) + 4) % 7));
};

/** Instant correspondant à une heure murale de Paris ; null si l'heure est mal formée. */
export function parisWallTime(year: number, month: number, day: number, hhmm: string): Date | null {
    const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
    if (!m) return null;
    const wall = Date.UTC(year, month - 1, day, Number(m[1]), Number(m[2]));
    if (Number.isNaN(wall)) return null;
    const summer = wall >= lastSundayUtc(year, 3) + 2 * HOUR_MS && wall < lastSundayUtc(year, 10) + 3 * HOUR_MS;
    return new Date(wall - (summer ? 2 : 1) * HOUR_MS);
}

/** Jour de Paris ("YYYY-MM-DD") d'un instant. */
export function parisDay(instant: Date): string {
    const t = instant.getTime();
    const year = instant.getUTCFullYear();
    const summer = t >= lastSundayUtc(year, 3) + HOUR_MS && t < lastSundayUtc(year, 10) + HOUR_MS;
    const wall = new Date(t + (summer ? 2 : 1) * HOUR_MS);
    return `${wall.getUTCFullYear()}-${pad2(wall.getUTCMonth() + 1)}-${pad2(wall.getUTCDate())}`;
}

const toJsDate = (value: unknown): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    const ts = value as { toDate?: () => Date; seconds?: number };
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    return null;
};

// Les promos créées avant l'ajout du champ `date` valent pour leur jour de création.
const promoDay = (p: FlashPromoTiming): string | null => {
    if (p.date) return p.date;
    const created = toJsDate(p.createdAt);
    return created && !Number.isNaN(created.getTime()) ? parisDay(created) : null;
};

/**
 * Début et fin effectifs, ou null si la promo n'a pas de créneau valide. Fin avant (ou égale
 * à) son début : le lendemain, jour calendaire suivant recalculé en heure de Paris.
 */
export function flashPromoWindow(p: FlashPromoTiming): { start: Date; end: Date } | null {
    const day = promoDay(p);
    const parts = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
    if (!parts || !p.startTime || !p.endTime) return null;
    const [y, m, d] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
    const start = parisWallTime(y, m, d, p.startTime);
    let end = parisWallTime(y, m, d, p.endTime);
    if (!start || !end) return null;
    if (end <= start) {
        const next = new Date(Date.UTC(y, m - 1, d + 1));
        end = parisWallTime(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), p.endTime);
        if (!end) return null;
    }
    return { start, end };
}

export function getFlashPromoStatus(p: FlashPromoTiming, now: Date = new Date()): FlashPromoStatus {
    if (p.active === false) return 'stopped';
    const window = flashPromoWindow(p);
    if (!window) return 'ended';
    const { start, end } = window;
    if (now >= end) return 'ended';
    if (typeof p.quantityRemaining === 'number' && p.quantityRemaining <= 0) return 'soldout';
    return now < start ? 'upcoming' : 'live';
}

export function isFlashPromoVisible(p: FlashPromoTiming, now: Date = new Date()): boolean {
    const status = getFlashPromoStatus(p, now);
    return status === 'live' || status === 'upcoming';
}

/** Jour de Paris d'aujourd'hui (date par défaut d'une nouvelle vente flash). */
export function todayISO(now: Date = new Date()): string {
    return parisDay(now);
}

export function formatFlashPromoDate(p: FlashPromoTiming): string {
    const date = promoDay(p);
    if (!date) return 'Date inconnue';
    if (date === todayISO()) return "Aujourd'hui";
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

// ── Réservation d'une offre : le client choisit le mode ; `fulfillment` absent = à emporter ──

export const flashFulfillment = (r: { fulfillment?: unknown }): 'takeaway' | 'dine_in' =>
    (r.fulfillment === 'dine_in' ? 'dine_in' : 'takeaway');

/** Nombre d'offres d'une réservation (1 pour les anciennes réservations). */
export const flashOfferQuantity = (r: { offerQuantity?: unknown }): number => {
    const n = Math.round(Number(r.offerQuantity));
    return n >= 1 ? n : 1;
};

/** Stock après un refus ou une annulation : toute la quantité revient, sans dépasser le total. */
export function restoredFlashStock(p: { quantityRemaining?: number; quantityTotal?: number }, quantity: number): number {
    const stock = (p.quantityRemaining || 0) + quantity;
    return p.quantityTotal ? Math.min(stock, p.quantityTotal) : stock;
}

/**
 * Campagne créée avec une vente flash : `flashPromoId` déclenche l'unique push, envoyé par
 * firestoreService.createCampaign à la création (une modification ne repasse pas par ici).
 * `targetSegment` : valeur comprise par sendCampaignPush ('all' ou clé de segment).
 */
export function flashCampaignDraft(
    promo: { restaurantId: number | string; itemName: string; discountPrice: number; originalPrice: number; targetSegment?: string },
    flashPromoId: string,
) {
    return {
        restaurantId: promo.restaurantId,
        name: `⚡ Flash : ${promo.itemName}`,
        type: 'flash' as const,
        status: 'active' as const,
        description: `Vente flash sur ${promo.itemName} ! ${promo.discountPrice}€ au lieu de ${promo.originalPrice}€.`,
        targetSegment: promo.targetSegment || 'all',
        flashPromoId,
    };
}

export const FLASH_PROMO_STATUS_LABEL: Record<FlashPromoStatus, string> = {
    upcoming: 'À venir',
    live: 'En cours',
    soldout: 'Épuisée',
    ended: 'Terminée',
    stopped: 'Arrêtée',
};
